import { Fragment, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ShieldOff, Star, RefreshCw, Flame, ChevronDown, ChevronRight, Play, X } from 'lucide-react';
import { useTenantScope } from '../context/TenantScopeContext';
import { useAuth } from '../context/AuthContext';
import { ColumnDef, DraggableTh, useColumnPrefs } from '../components/ColumnPicker';

type Severity = 'low' | 'medium' | 'high' | 'critical';
type VulnType = 'eol_os' | 'vmware_advisory' | 'vmware_posture' | 'software_cve' | 'eol_software' | 'epp_missing_patch' | 'acronis_va';

// Rilievi "per prodotto": raggruppabili nella vista per software. acronis_va porta il numero di
// vulnerabilità che dà Acronis (in cveCount) e le patch mancanti, non l'elenco dei CVE.
const PRODUCT_TYPES: VulnType[] = ['software_cve', 'eol_software', 'acronis_va'];

interface CveSummary {
  id: string;
  score: number | null;
  kev: boolean;
  published: string;
  summary: string;
}

// Dettaglio strutturato dei rilievi sul software installato (backend services/vuln/findings.ts).
// Nell'elenco arriva senza `cves`, che si carica aprendo il singolo rilievo.
interface SoftwareData {
  product: string;
  label: string;
  version: string;
  cveCount?: number;
  kevCount?: number;
  maxScore?: number | null;
  cves?: CveSummary[];
  note?: string;
  otherVersions?: string[];
  eolDate?: string;
  // acronis_va
  vendor?: string;
  scanType?: 'os' | 'third_party';
  scannedAt?: string;
  patches?: string[];
  severityReason?: string;
  possibleKev?: Array<{ id: string; published: string; summary: string }>;
}

interface Vulnerability {
  id: number;
  asset_id: number;
  hostname: string;
  business_critical: boolean;
  type: VulnType;
  ref: string;
  title: string;
  severity: Severity;
  detail: string | null;
  detected_at: string;
  tenant_name: string;
  data: SoftwareData | null;
}

interface SoftwareCheckStatus {
  running: boolean;
  lastRun: { finishedAt: string; findings: number; products: number; failedSources: string[] } | null;
  sources: { cpe: string; cve_count: number; fetched_at: string | null; last_error: string | null }[];
}

const SEVERITIES: Severity[] = ['critical', 'high', 'medium', 'low'];
const SEVERITY_RANK: Record<Severity, number> = { critical: 4, high: 3, medium: 2, low: 1 };
const SEVERITY_BADGE: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600', medium: 'bg-amber-100 text-amber-700',
  high: 'bg-orange-100 text-orange-700', critical: 'bg-red-100 text-red-700',
};

// Tipi di rilievo: OS fuori supporto (jobs/vulnerabilityCheck.ts), bollettini VMSA per
// versione/build e configurazioni deboli di host/VM vSphere (jobs/vmwareSync.ts), software
// installato con CVE noti o fuori supporto (jobs/softwareVulnCheck.ts).
const TYPE_LABELS: Record<VulnType, string> = {
  eol_os: 'End of life (OS)', vmware_advisory: 'VMSA advisory', vmware_posture: 'Configuration',
  software_cve: 'Software CVE', eol_software: 'End of life (software)',
  epp_missing_patch: 'Missing patch (Sangfor EPP)', acronis_va: 'Acronis assessment',
};

// Patch mancanti di un rilievo del vulnerability assessment Acronis (già nell'elenco, nessuna
// chiamata in più).
function PatchList({ data }: { data: SoftwareData }) {
  const patches = data.patches || [];
  const age = data.scannedAt ? Math.floor((Date.now() - new Date(data.scannedAt).getTime()) / 86400000) : null;
  return (
    <div className="text-xs text-gray-600">
      <p className="text-gray-500 mb-2">
        Acronis {data.scanType === 'os' ? 'Windows' : 'third-party applications'} scan
        {data.scannedAt ? ` of ${new Date(data.scannedAt).toLocaleString('en-GB')}` : ''}
        {age !== null ? <span className={age > 14 ? 'text-amber-700 font-medium' : ''}> ({age} day{age === 1 ? '' : 's'} ago)</span> : null}
        : {data.cveCount ?? 0} known vulnerabilities in {data.label}. Acronis reports the count only, not the CVE list
        or the installed version.
        {data.severityReason ? ` Severity: ${data.severityReason}.` : ''}
      </p>
      {data.possibleKev && data.possibleKev.length > 0 && (
        <div className="mb-2 rounded border border-red-100 bg-red-50 px-3 py-2">
          <p className="text-red-700 font-medium inline-flex items-center gap-1"><Flame size={11} /> Possibly exploited in the wild</p>
          <p className="text-gray-600">The missing update fixes these vulnerabilities from the CISA KEV catalog. They apply only if the installed version is older than their fix.</p>
          <ul className="list-disc pl-5 mt-1 space-y-0.5">
            {data.possibleKev.map(k => (
              <li key={k.id}>
                <a href={`https://nvd.nist.gov/vuln/detail/${k.id}`} target="_blank" rel="noreferrer" className="font-mono text-primary-600 hover:underline">{k.id}</a>
                <span className="text-gray-500"> ({k.published}) {k.summary}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {patches.length > 0
        ? <ul className="list-disc pl-5 space-y-0.5">{patches.map(p => <li key={p}>{p}</li>)}</ul>
        : <p className="text-gray-400">No missing update listed by Acronis for this product.</p>}
    </div>
  );
}

const COLUMNS: ColumnDef[] = [
  { key: 'severity', label: 'Severity', defaultVisible: true },
  { key: 'type', label: 'Type', defaultVisible: true },
  { key: 'asset', label: 'Asset', defaultVisible: true },
  { key: 'tenant', label: 'Tenant', defaultVisible: true },
  { key: 'title', label: 'Finding', defaultVisible: true },
  { key: 'cves', label: 'CVEs', defaultVisible: true },
  { key: 'exploited', label: 'Exploited', defaultVisible: true },
  { key: 'cvss', label: 'Max CVSS', defaultVisible: true },
  { key: 'detected_at', label: 'Detected', defaultVisible: true },
  { key: 'detail', label: 'Detail', defaultVisible: true },
];

const SOFTWARE_COLUMNS: ColumnDef[] = [
  { key: 'severity', label: 'Worst severity', defaultVisible: true },
  { key: 'software', label: 'Software', defaultVisible: true },
  { key: 'type', label: 'Type', defaultVisible: true },
  { key: 'assets', label: 'Assets', defaultVisible: true },
  { key: 'versions', label: 'Versions', defaultVisible: true },
  { key: 'exploited', label: 'Exploited CVEs', defaultVisible: true },
  { key: 'cvss', label: 'Max CVSS', defaultVisible: true },
];

const CVE_COLUMNS: ColumnDef[] = [
  { key: 'id', label: 'CVE', defaultVisible: true },
  { key: 'score', label: 'CVSS', defaultVisible: true },
  { key: 'exploited', label: 'Exploited', defaultVisible: true },
  { key: 'published', label: 'Published', defaultVisible: true },
  { key: 'summary', label: 'Summary', defaultVisible: true },
];

// Oltre questa soglia la tabella mostra solo le prime righe (già ordinate per gravità) finché
// l'utente non chiede il resto: migliaia di righe tutte insieme rallentano la pagina senza
// aggiungere nulla a chi deve decidere da dove partire.
const PAGE_LIMIT = 300;

function ExploitedBadge({ count }: { count: number }) {
  return (
    <span className="badge bg-red-50 text-red-700 inline-flex items-center gap-1 whitespace-nowrap" title="Listed in the CISA Known Exploited Vulnerabilities catalog">
      <Flame size={11} /> {count}
    </span>
  );
}

// Elenco dei CVE più rilevanti di un rilievo software, caricato all'apertura della riga.
function CveList({ vulnerabilityId }: { vulnerabilityId: number }) {
  const { orderedDefs, moveColumn } = useColumnPrefs('soc.columns.vulnerability_cves', CVE_COLUMNS);
  const [data, setData] = useState<SoftwareData | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`/api/vulnerabilities/${vulnerabilityId}`)
      .then(res => { if (!cancelled) setData(res.data.data.data); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [vulnerabilityId]);

  if (failed) return <p className="text-xs text-red-600">Could not load the CVE list.</p>;
  if (!data) return <p className="text-xs text-gray-400">Loading CVEs…</p>;
  const cves = data.cves || [];

  function renderCell(cve: CveSummary, key: string) {
    switch (key) {
      case 'id':
        return (
          <a href={`https://nvd.nist.gov/vuln/detail/${cve.id}`} target="_blank" rel="noreferrer"
            className="font-mono text-xs text-primary-600 hover:underline whitespace-nowrap">{cve.id}</a>
        );
      case 'score':
        return <span className="text-gray-700">{cve.score !== null ? cve.score.toFixed(1) : '—'}</span>;
      case 'exploited':
        return cve.kev
          ? <span className="badge bg-red-50 text-red-700 inline-flex items-center gap-1 whitespace-nowrap"><Flame size={11} /> in the wild</span>
          : <span className="text-gray-300">—</span>;
      case 'published':
        return <span className="text-gray-500 whitespace-nowrap">{cve.published}</span>;
      case 'summary':
        return <span className="text-gray-600 text-xs">{cve.summary}</span>;
      default:
        return null;
    }
  }

  return (
    <div>
      <p className="text-xs text-gray-500 mb-2">
        {(data.cveCount ?? cves.length) > cves.length
          ? `Showing the ${cves.length} most relevant of ${(data.cveCount ?? 0).toLocaleString('en-US')} CVEs (exploited first, then by CVSS).`
          : `${cves.length} CVE${cves.length === 1 ? '' : 's'} affecting this version.`}
        {data.otherVersions?.length ? ` Also installed on this asset: ${data.otherVersions.join(', ')}.` : ''}
        {data.note ? ` ${data.note}` : ''}
      </p>
      <table className="w-full text-sm bg-white border border-gray-100 rounded">
        <thead>
          <tr>
            {orderedDefs.map(d => (
              <DraggableTh key={d.key} colKey={d.key} onMove={moveColumn} className="table-header">{d.label}</DraggableTh>
            ))}
          </tr>
        </thead>
        <tbody>
          {cves.map(cve => (
            <tr key={cve.id} className="border-t border-gray-100">
              {orderedDefs.map(d => <td key={d.key} className="table-cell align-top">{renderCell(cve, d.key)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface SoftwareGroup {
  key: string;
  label: string;
  type: VulnType;
  assets: number;
  versions: Set<string>;
  severity: Severity;
  maxKev: number;
  maxScore: number | null;
}

// Vulnerabilità aperte: OS e software fuori supporto, bollettini e postura VMware, e — dal
// 2026-10-01 — le versioni di software installato colpite da CVE noti (un rilievo per asset e
// prodotto, vedi backend jobs/softwareVulnCheck.ts). "critical" su un rilievo software significa
// che almeno un CVE è sfruttato in attacchi reali (catalogo CISA KEV), non solo CVSS alto.
export default function VulnerabilitiesPage() {
  const { selectedTenantId } = useTenantScope();
  const { user } = useAuth();
  const { orderedDefs, moveColumn } = useColumnPrefs('soc.columns.vulnerabilities', COLUMNS);
  const softwareCols = useColumnPrefs('soc.columns.vulnerabilities_software', SOFTWARE_COLUMNS);
  const [items, setItems] = useState<Vulnerability[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'findings' | 'software'>('findings');
  const [severity, setSeverity] = useState<Severity | ''>('');
  const [type, setType] = useState<VulnType | ''>('');
  const [search, setSearch] = useState('');
  const [exploitedOnly, setExploitedOnly] = useState(false);
  const [product, setProduct] = useState<{ key: string; label: string } | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [check, setCheck] = useState<SoftwareCheckStatus | null>(null);
  const [starting, setStarting] = useState(false);

  async function load() {
    setLoading(true);
    const [list, status] = await Promise.all([
      axios.get('/api/vulnerabilities', { params: { tenantId: selectedTenantId ?? undefined } }),
      axios.get('/api/vulnerabilities/software-check').catch(() => null),
    ]);
    setItems(list.data.data);
    setCheck(status ? status.data.data : null);
    setLoading(false);
  }

  useEffect(() => { load(); }, [selectedTenantId]);
  useEffect(() => { setShowAll(false); setExpanded(null); }, [severity, type, search, exploitedOnly, product, view]);

  async function runCheck() {
    setStarting(true);
    try {
      await axios.post('/api/vulnerabilities/software-check/run');
      setCheck(c => (c ? { ...c, running: true } : c));
    } catch { /* 409: un controllo è già in corso, lo stato lo mostra al prossimo refresh */ }
    setStarting(false);
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(v => {
      if (severity && v.severity !== severity) return false;
      if (type && v.type !== type) return false;
      if (exploitedOnly && !(v.data?.kevCount && v.data.kevCount > 0)) return false;
      if (product && !(v.data?.product === product.key && PRODUCT_TYPES.includes(v.type))) return false;
      if (q && !`${v.hostname} ${v.title} ${v.tenant_name}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, severity, type, search, exploitedOnly, product]);

  const severityCounts = useMemo(() => {
    const counts: Record<Severity, number> = { critical: 0, high: 0, medium: 0, low: 0 };
    for (const v of items) counts[v.severity]++;
    return counts;
  }, [items]);
  const exploitedCount = useMemo(() => items.filter(v => (v.data?.kevCount ?? 0) > 0).length, [items]);
  const presentTypes = useMemo(() => [...new Set(items.map(v => v.type))], [items]);

  // Vista per software: gli stessi rilievi raggruppati per prodotto, per rispondere a "cosa
  // aggiorno per primo su tutto il parco" invece che macchina per macchina.
  const groups = useMemo(() => {
    const map = new Map<string, SoftwareGroup>();
    for (const v of filtered) {
      if (!v.data || !PRODUCT_TYPES.includes(v.type)) continue;
      const key = `${v.type}:${v.data.product}`;
      const group = map.get(key) || {
        key: v.data.product, label: v.data.label, type: v.type, assets: 0, versions: new Set<string>(),
        severity: v.severity, maxKev: 0, maxScore: null,
      };
      group.assets++;
      if (v.data.version) group.versions.add(v.data.version);
      if (SEVERITY_RANK[v.severity] > SEVERITY_RANK[group.severity]) group.severity = v.severity;
      group.maxKev = Math.max(group.maxKev, v.data.kevCount ?? 0);
      if (typeof v.data.maxScore === 'number') group.maxScore = Math.max(group.maxScore ?? 0, v.data.maxScore);
      map.set(key, group);
    }
    return [...map.values()].sort((a, b) =>
      SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity] || b.maxKev - a.maxKev || b.assets - a.assets);
  }, [filtered]);

  const shown = showAll ? filtered : filtered.slice(0, PAGE_LIMIT);

  function renderCell(v: Vulnerability, key: string) {
    switch (key) {
      case 'severity':
        return <span className={`badge ${SEVERITY_BADGE[v.severity]}`}>{v.severity}</span>;
      case 'type':
        return <span className="badge bg-gray-100 text-gray-500 whitespace-nowrap">{TYPE_LABELS[v.type] || v.type}</span>;
      case 'asset':
        return (
          <Link to={`/asset-center/${v.asset_id}`} onClick={e => e.stopPropagation()}
            className="inline-flex items-center gap-1.5 font-medium text-primary-600 hover:underline">
            {v.business_critical && <Star size={11} className="text-amber-500" fill="currentColor" />}
            {v.hostname}
          </Link>
        );
      case 'tenant':
        return <span className="text-gray-500">{v.tenant_name}</span>;
      case 'title':
        return (
          <span className="text-gray-800 inline-flex items-start gap-1 min-w-[18rem]">
            {(v.type === 'software_cve' || v.type === 'acronis_va') && (expanded === v.id
              ? <ChevronDown size={14} className="mt-0.5 shrink-0 text-gray-400" />
              : <ChevronRight size={14} className="mt-0.5 shrink-0 text-gray-400" />)}
            {v.title}
          </span>
        );
      case 'cves':
        return typeof v.data?.cveCount === 'number'
          ? <span className="text-gray-700">{v.data.cveCount.toLocaleString('en-US')}</span>
          : <span className="text-gray-300">—</span>;
      case 'exploited':
        return v.data?.kevCount ? <ExploitedBadge count={v.data.kevCount} /> : <span className="text-gray-300">—</span>;
      case 'cvss':
        return typeof v.data?.maxScore === 'number'
          ? <span className="text-gray-700">{v.data.maxScore.toFixed(1)}</span>
          : <span className="text-gray-300">—</span>;
      case 'detail':
        // Per i rilievi software il testo ripete ciò che è già nelle colonne CVE/Exploited/CVSS e
        // nell'elenco che si apre cliccando la riga: qui resta solo ciò che le colonne non dicono.
        if (v.type === 'acronis_va') {
          const n = v.data?.patches?.length ?? 0;
          return <span className="text-gray-500 text-xs">{n ? `${n} missing update${n === 1 ? '' : 's'} — click to list` : 'No matching update listed'}</span>;
        }
        if (v.type === 'software_cve') {
          return v.data?.otherVersions?.length
            ? <span className="text-gray-500 text-xs">Also installed: {v.data.otherVersions.join(', ')}</span>
            : <span className="text-gray-300">—</span>;
        }
        return <span className="text-gray-500 text-xs">{v.detail || '—'}</span>;
      case 'detected_at':
        return <span className="text-gray-500 whitespace-nowrap">{new Date(v.detected_at).toLocaleDateString('en-GB')}</span>;
      default:
        return null;
    }
  }

  function renderGroupCell(g: SoftwareGroup, key: string) {
    switch (key) {
      case 'severity':
        return <span className={`badge ${SEVERITY_BADGE[g.severity]}`}>{g.severity}</span>;
      case 'software':
        return <span className="font-medium text-primary-600">{g.label}</span>;
      case 'type':
        return <span className="badge bg-gray-100 text-gray-500 whitespace-nowrap">{TYPE_LABELS[g.type]}</span>;
      case 'assets':
        return <span className="text-gray-800">{g.assets}</span>;
      case 'versions':
        return <span className="text-gray-500">{g.versions.size || '—'}</span>;
      case 'exploited':
        return g.maxKev ? <ExploitedBadge count={g.maxKev} /> : <span className="text-gray-300">—</span>;
      case 'cvss':
        return g.maxScore !== null ? <span className="text-gray-700">{g.maxScore.toFixed(1)}</span> : <span className="text-gray-300">—</span>;
      default:
        return null;
    }
  }

  const failedSources = check?.sources.filter(s => s.last_error) ?? [];
  const lastFetch = check?.sources.map(s => s.fetched_at).filter((d): d is string => !!d).sort().pop();

  return (
    <div>
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 inline-flex items-center gap-2">
            <ShieldOff size={20} /> Vulnerabilities
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Installed software affected by known CVEs (matched against NVD for a curated list of products) or no longer
            supported, unsupported operating systems, and — for VMware vSphere connections — VMSA advisories and weak
            host/VM configurations. A software finding is <span className="font-medium">critical</span> when at least
            one of its CVEs is exploited in the wild (CISA KEV catalog). Acronis assessment findings come from the
            vulnerability scans Acronis runs on each machine: Acronis gives the number of vulnerabilities per product and
            the missing updates, not the CVE list: for Windows the severity follows the age of the last installed update, for
            applications the count, one level higher for browsers, runtimes, remote access tools, documents and archives, and
            high when the missing update fixes vulnerabilities exploited in the wild.
          </p>
        </div>
        <button onClick={load} disabled={loading} className="btn-secondary flex items-center gap-1 disabled:opacity-50 shrink-0">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {check && (
        <div className="card px-4 py-2 mb-3 text-xs text-gray-500 flex items-center gap-3 flex-wrap">
          <span>
            Software CVE data: {check.sources.filter(s => s.fetched_at).length} products from NVD
            {lastFetch ? `, last updated ${new Date(lastFetch).toLocaleString('en-GB')}` : ', not downloaded yet'}.
          </span>
          {check.running && <span className="text-primary-600">Check in progress…</span>}
          {failedSources.length > 0 && (
            <span className="text-amber-700" title={failedSources.map(s => `${s.cpe}: ${s.last_error}`).join('\n')}>
              {failedSources.length} product{failedSources.length === 1 ? '' : 's'} could not be refreshed (previous data kept).
            </span>
          )}
          {user?.scopeLevel === 'platform' && (
            <button onClick={runCheck} disabled={starting || check.running}
              className="btn-secondary ml-auto flex items-center gap-1 disabled:opacity-50">
              <Play size={12} /> Run check now
            </button>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {SEVERITIES.map(s => (
          <button key={s} onClick={() => setSeverity(severity === s ? '' : s)}
            className={`badge ${SEVERITY_BADGE[s]} ${severity === s ? 'ring-2 ring-primary-400' : ''}`}>
            {s} {severityCounts[s]}
          </button>
        ))}
        <button onClick={() => setExploitedOnly(!exploitedOnly)}
          className={`badge bg-red-50 text-red-700 inline-flex items-center gap-1 ${exploitedOnly ? 'ring-2 ring-primary-400' : ''}`}>
          <Flame size={11} /> exploited in the wild {exploitedCount}
        </button>
        <select value={type} onChange={e => setType(e.target.value as VulnType | '')} className="input-field text-sm py-1 w-52">
          <option value="">All types</option>
          {presentTypes.map(t => <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>)}
        </select>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search asset or finding…"
          className="input-field text-sm py-1 w-56" />
        {product && (
          <span className="badge bg-primary-50 text-primary-700 inline-flex items-center gap-1">
            {product.label}
            <button onClick={() => setProduct(null)} aria-label="Clear software filter"><X size={11} /></button>
          </span>
        )}
        <div className="ml-auto inline-flex rounded-lg border border-gray-200 overflow-hidden text-sm">
          {(['findings', 'software'] as const).map(v => (
            <button key={v} onClick={() => setView(v)}
              className={`px-3 py-1 ${view === v ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              {v === 'findings' ? 'By asset' : 'By software'}
            </button>
          ))}
        </div>
      </div>

      {view === 'software' ? (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr>
                {softwareCols.orderedDefs.map(d => (
                  <DraggableTh key={d.key} colKey={d.key} onMove={softwareCols.moveColumn} className="table-header">{d.label}</DraggableTh>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 && (
                <tr><td className="table-cell text-gray-400" colSpan={softwareCols.orderedDefs.length}>
                  {loading ? 'Loading…' : 'No software finding matches the current filters.'}
                </td></tr>
              )}
              {groups.map(g => (
                <tr key={`${g.type}:${g.key}`} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                  onClick={() => { setProduct({ key: g.key, label: g.label }); setType(g.type); setView('findings'); }}>
                  {softwareCols.orderedDefs.map(d => <td key={d.key} className="table-cell">{renderGroupCell(g, d.key)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                {orderedDefs.map(d => (
                  <DraggableTh key={d.key} colKey={d.key} onMove={moveColumn} className="table-header">
                    {d.label}
                  </DraggableTh>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr><td className="table-cell text-gray-400" colSpan={orderedDefs.length}>Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td className="table-cell text-gray-400" colSpan={orderedDefs.length}>
                  {items.length === 0 ? 'No open vulnerability on synced assets.' : 'No vulnerability matches the current filters.'}
                </td></tr>
              )}
              {shown.map(v => (
                <Fragment key={v.id}>
                  <tr className={`border-t border-gray-100 hover:bg-gray-50 ${v.type === 'software_cve' || v.type === 'acronis_va' ? 'cursor-pointer' : ''}`}
                    onClick={() => { if (v.type === 'software_cve' || v.type === 'acronis_va') setExpanded(expanded === v.id ? null : v.id); }}>
                    {orderedDefs.map(d => <td key={d.key} className="table-cell align-top">{renderCell(v, d.key)}</td>)}
                  </tr>
                  {expanded === v.id && (
                    <tr className="bg-gray-50">
                      <td className="px-6 py-3" colSpan={orderedDefs.length}>
                        {v.type === 'acronis_va' && v.data ? <PatchList data={v.data} /> : <CveList vulnerabilityId={v.id} />}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
          {filtered.length > shown.length && (
            <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-500 flex items-center gap-2">
              Showing the first {shown.length} of {filtered.length.toLocaleString('en-US')} findings, most severe first.
              <button onClick={() => setShowAll(true)} className="text-primary-600 hover:underline">Show all</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
